import { useState } from "react";
import useSWR from "swr";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Pencil } from "lucide-react";

const fetcher = (url) => http.get(url).then((r) => r.data);

const COLORS = ["#2563EB", "#8B5CF6", "#F59E0B", "#0EA5E9", "#10B981", "#F43F5E", "#64748B", "#EA580C"];

function ColorPicker({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {COLORS.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          className={`w-7 h-7 rounded-full ring-offset-2 transition-shadow ${value === c ? "ring-2 ring-blue-500" : ""}`}
          style={{ background: c }}
          data-testid={`color-${c}`}
          aria-label={c}
        />
      ))}
    </div>
  );
}

export default function Kategori() {
  const { data: cats, mutate: mutCats } = useSWR("/categories", fetcher);
  const { data: assets, mutate: mutAssets } = useSWR("/assets", fetcher);

  const [catForm, setCatForm] = useState({ open: false, edit: null, name: "", color: "#2563EB" });
  const [assetForm, setAssetForm] = useState({ open: false, edit: null, name: "", category_id: "", notes: "" });

  const saveCat = async () => {
    if (!catForm.name.trim()) return toast.error("Nama kategori wajib diisi");
    try {
      const payload = { name: catForm.name.trim(), color: catForm.color, icon: "Wallet" };
      if (catForm.edit) await http.patch(`/categories/${catForm.edit}`, payload);
      else await http.post("/categories", payload);
      toast.success("Kategori disimpan");
      setCatForm({ open: false, edit: null, name: "", color: "#2563EB" });
      mutCats();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const delCat = async (id) => {
    if (!window.confirm("Hapus kategori dan semua aset di dalamnya?")) return;
    try {
      await http.delete(`/categories/${id}`);
      toast.success("Kategori dihapus");
      mutCats();
      mutAssets();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const saveAsset = async () => {
    if (!assetForm.name.trim() || !assetForm.category_id) return toast.error("Nama & kategori wajib diisi");
    try {
      const payload = { name: assetForm.name.trim(), category_id: assetForm.category_id, notes: assetForm.notes };
      if (assetForm.edit) await http.patch(`/assets/${assetForm.edit}`, payload);
      else await http.post("/assets", payload);
      toast.success("Aset disimpan");
      setAssetForm({ open: false, edit: null, name: "", category_id: "", notes: "" });
      mutAssets();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const delAsset = async (id) => {
    if (!window.confirm("Hapus aset dan semua riwayat saldonya?")) return;
    try {
      await http.delete(`/assets/${id}`);
      toast.success("Aset dihapus");
      mutAssets();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-8">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Manajemen</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            Kategori & Aset
          </h1>
          <p className="mt-1 text-slate-600">Kelola kelompok aset dan item di dalamnya.</p>
        </div>
      </header>

      {/* Kategori */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl font-medium text-slate-900">Kategori</h2>
          <Dialog open={catForm.open} onOpenChange={(o) => setCatForm({ ...catForm, open: o, edit: o ? catForm.edit : null })}>
            <DialogTrigger asChild>
              <Button
                className="bg-blue-600 hover:bg-blue-700 gap-2"
                data-testid="add-category-button"
                onClick={() => setCatForm({ open: true, edit: null, name: "", color: "#2563EB" })}
              >
                <Plus className="w-4 h-4" /> Tambah Kategori
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="category-modal">
              <DialogHeader>
                <DialogTitle>{catForm.edit ? "Edit Kategori" : "Tambah Kategori"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Nama</Label>
                  <Input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} data-testid="category-name-input" />
                </div>
                <div>
                  <Label>Warna</Label>
                  <ColorPicker value={catForm.color} onChange={(c) => setCatForm({ ...catForm, color: c })} />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={saveCat} className="bg-blue-600 hover:bg-blue-700" data-testid="category-save-button">Simpan</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(cats || []).map((c) => (
            <Card key={c.id} className="p-5 card-hover flex items-center gap-3" data-testid={`category-card-${c.id}`}>
              <span className="w-3 h-3 rounded-full shrink-0" style={{ background: c.color }} />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 truncate">{c.name}</div>
                <div className="text-xs text-slate-500">
                  {(assets || []).filter((a) => a.category_id === c.id).length} aset
                </div>
              </div>
              <Button variant="ghost" size="icon" className="text-slate-500 hover:text-slate-900"
                onClick={() => setCatForm({ open: true, edit: c.id, name: c.name, color: c.color })}
                data-testid={`edit-category-${c.id}`}>
                <Pencil className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="text-slate-500 hover:text-rose-600"
                onClick={() => delCat(c.id)} data-testid={`delete-category-${c.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </Card>
          ))}
        </div>
      </section>

      {/* Aset */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl font-medium text-slate-900">Aset</h2>
          <Dialog open={assetForm.open} onOpenChange={(o) => setAssetForm({ ...assetForm, open: o, edit: o ? assetForm.edit : null })}>
            <DialogTrigger asChild>
              <Button
                className="bg-blue-600 hover:bg-blue-700 gap-2"
                data-testid="add-asset-button"
                onClick={() => setAssetForm({ open: true, edit: null, name: "", category_id: "", notes: "" })}
              >
                <Plus className="w-4 h-4" /> Tambah Aset
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="asset-modal">
              <DialogHeader>
                <DialogTitle>{assetForm.edit ? "Edit Aset" : "Tambah Aset"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Nama</Label>
                  <Input value={assetForm.name} onChange={(e) => setAssetForm({ ...assetForm, name: e.target.value })} data-testid="asset-name-input" placeholder="Contoh: BCA Tabungan" />
                </div>
                <div>
                  <Label>Kategori</Label>
                  <Select value={assetForm.category_id} onValueChange={(v) => setAssetForm({ ...assetForm, category_id: v })}>
                    <SelectTrigger data-testid="asset-category-select"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                    <SelectContent>
                      {(cats || []).map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Catatan</Label>
                  <Input value={assetForm.notes} onChange={(e) => setAssetForm({ ...assetForm, notes: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={saveAsset} className="bg-blue-600 hover:bg-blue-700" data-testid="asset-save-button">Simpan</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="overflow-hidden">
          <div className="grid grid-cols-12 px-6 py-3 border-b border-slate-100 text-xs uppercase tracking-[0.1em] font-semibold text-slate-500 bg-slate-50/60">
            <div className="col-span-5">Aset</div>
            <div className="col-span-4">Kategori</div>
            <div className="col-span-3 text-right">Aksi</div>
          </div>
          <div className="divide-y divide-slate-100">
            {(assets || []).length === 0 && (
              <div className="px-6 py-10 text-center text-slate-500 text-sm">Belum ada aset. Tambahkan aset pertama Anda.</div>
            )}
            {(assets || []).map((a) => {
              const c = (cats || []).find((x) => x.id === a.category_id);
              return (
                <div key={a.id} className="grid grid-cols-12 px-6 py-3 items-center" data-testid={`asset-row-${a.id}`}>
                  <div className="col-span-5">
                    <div className="text-sm font-medium text-slate-800">{a.name}</div>
                    {a.notes && <div className="text-xs text-slate-500">{a.notes}</div>}
                  </div>
                  <div className="col-span-4 flex items-center gap-2 text-sm text-slate-700">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: c?.color || "#94A3B8" }} />
                    {c?.name || "-"}
                  </div>
                  <div className="col-span-3 flex justify-end gap-1">
                    <Button variant="ghost" size="icon" className="text-slate-500 hover:text-slate-900"
                      onClick={() => setAssetForm({ open: true, edit: a.id, name: a.name, category_id: a.category_id, notes: a.notes || "" })}
                      data-testid={`edit-asset-${a.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-slate-500 hover:text-rose-600"
                      onClick={() => delAsset(a.id)} data-testid={`delete-asset-${a.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </section>
    </div>
  );
}
